import { createLogic } from "redux-logic";
import * as DOMUtils from "history/DOMUtils";
import Debug from "../../../../../common-deprecated/Debug";
import { configureCtxConfig } from "../../../../shared-logic/utils/saveUtils";
import type { CarConfigLogicType } from "../store";
import { errorConfigSave, finishConfigSave, startConfigSave } from "../slices/CarConfigSaveSlice";
import { getExternalConfigurationObject } from "../../utils/typedUtils";
import { getLocalStorageParams, setUnfinishedConfiguration } from "../../utils/localStorage";
import { isRetailer } from "../../../../../common-deprecated/utils";
import { CarConfigConfigureSection } from "../../utils/types/SectionTypes";

const startSaveLogic = (createLogic as CarConfigLogicType<ReturnType<typeof startConfigSave>>)({
    type: startConfigSave.type,
    name: "startConfigSaveLogic",
    latest: true,
    validate({ getState, action }, allow, reject) {
        const isToyotaRetailer = isRetailer(getState().commonSettings) && getState().commonSettings.brand === "toyota";
        const isOverview = getState().carConfigNavigation.activeSection === CarConfigConfigureSection.Summary;

        if (
            getState().save.saving ||
            !getState().car.initialized ||
            !getState().items.initialized ||
            (isToyotaRetailer && !isOverview)
        ) {
            // Only allow config saving when the config isn't saving currently.
            (reject as () => void)();
        } else {
            allow(action);
        }
    },

    async process({ getState }, dispatch, done) {
        const { carConfigSettings } = getState();
        const config = getExternalConfigurationObject(getState());

        try {
            const fallbackGateway = `${carConfigSettings.loadSaveServer}/config/find/json/legacy`;
            const fetchResult = await fetch(fallbackGateway, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(config),
                mode: "cors",
            });

            const savingConfig = await fetchResult.json();

            // This logic is for handling the CTA's, required for the logic of GFI:
            configureCtxConfig(savingConfig);

            dispatch(finishConfigSave({ data: savingConfig }));
        } catch (err) {
            Debug.error("Failed to save configuration", err);
            dispatch(errorConfigSave());
        }

        done();
    },
});

const finishSaveLogic = (createLogic as CarConfigLogicType<ReturnType<typeof finishConfigSave>>)({
    type: finishConfigSave.type,
    transform({ getState, action }, next) {
        const { commonSettings, carConfigSettings, carConfigNavigation } = getState();
        const { data } = action.payload;

        // Fallback if QrCode isn't provided by the response.
        const configCode = data.Personal ? data.Personal.Code : data.ConfigurationCode;

        if (DOMUtils.canUseDOM) {
            // Set unfinished configuration in the localStorage when changing the configuration.
            const localStorageParams = getLocalStorageParams({ commonSettings });
            setUnfinishedConfiguration(localStorageParams, configCode, carConfigNavigation.activeSection);
        }

        // the ${data}qrcode/${configCode} is used when loading a Toyota code, we utilize the direct URL for loading a toyota code (ex.: https://prev.ccsave.toyota-europe.com/config/BybIQxnJV/legacy) therefor the QrCode is not present in the data object
        const qrCode = data.QrCode || `${carConfigSettings.loadSaveServer}/qrcode/${String(configCode)}`;
        next(finishConfigSave({ data, qrCode }));
    },
});

export default [startSaveLogic, finishSaveLogic];
export const serverLogic = [finishSaveLogic];
